import React, { useState } from 'react';
import { exportJunctionScenarioExcel } from './junctionExcel';
import { evalFlowCell, isEditable, isBuiltIn, describe } from './flowScenarios';

// Junction turning matrices, surfaced read-only in Results (no need to open the
// schematic editor). The "cases" shown as tabs are user-composable FLOW
// SCENARIOS (see flowScenarios.js): two built-in leaves — Existing (surveyed,
// editable) and Development (the assignment for the selected development
// scenario) — plus any number the user builds by growth (base × factor) or sum
// (scenarios added together). Arms come from the development matrix so every
// scenario stays aligned.

const PU = '#3D1A6E', PU2 = '#7B4DBF', PU_SOFT = '#F5F0FA', PU_LINE = '#EDE5F8';
const INK = '#1C1C2E', MUT = '#6B6880', FAINT = '#C4BDD4', TEAL = '#0D9488';
const CORAL = '#D85A30', CORAL_INK = '#993C1D', CORAL_SOFT = '#FAECE7';
const TEAL_INK = '#0F6E56';

function num(v) { const n = parseInt(String(v).replace(/[^0-9-]/g, ''), 10); return isNaN(n) ? 0 : n; }

// Parse a copied Excel grid (tab/newline separated) and align it to `arms`
// by matching header labels — so column order in the survey file doesn't
// matter. Handles both an Excel-style empty top-left corner cell and its
// absence (the leading tab is easy to lose to trimming), by detecting the
// corner from row width rather than assuming it. Returns
// { grid, matchedRows, badCols } or null if unparseable.
function parsePaste(text, arms) {
  const raw = text.replace(/\r/g, '');
  const lines = raw.split('\n').filter(l => l.trim().length);
  if (lines.length < 2) return null;
  const norm = s => String(s).toLowerCase().replace(/[^a-z0-9]/g, '');
  const match = label => {
    const n = norm(label);
    let i = arms.findIndex(a => norm(a) === n);
    if (i >= 0) return i;
    return arms.findIndex(a => n && (norm(a).includes(n) || n.includes(norm(a))));
  };
  const rows = lines.slice(1).map(l => l.split('\t'));
  const dataWidth = Math.max(...rows.map(r => r.length));   // rowLabel + one cell per column
  const headerCells = lines[0].split('\t');
  // If the header is as wide as a data row it carries a corner cell; drop it.
  const colLabels = headerCells.length >= dataWidth ? headerCells.slice(1) : headerCells;
  const colMap = colLabels.map(match);
  const grid = arms.map(() => arms.map(() => 0));
  let matchedRows = 0;
  for (const cells of rows) {
    const ri = match(cells[0]);
    if (ri < 0) continue;
    matchedRows++;
    for (let j = 0; j < colMap.length; j++) {
      const ci = colMap[j];
      if (ci >= 0 && ci !== ri) grid[ri][ci] = num(cells[j + 1]);
    }
  }
  return { grid, matchedRows, badCols: colMap.filter(x => x < 0).length };
}

const th = { padding: '7px 10px', textAlign: 'center', border: `1px solid ${PU_LINE}`, background: PU, color: '#fff', fontWeight: 700, fontSize: 11 };
const rh = { padding: '7px 10px', textAlign: 'left', border: `1px solid ${PU_LINE}`, background: PU_SOFT, color: PU, fontWeight: 800, whiteSpace: 'nowrap' };
const td = { padding: '6px 8px', textAlign: 'center', border: `1px solid ${PU_LINE}`, fontWeight: 600, fontVariantNumeric: 'tabular-nums', height: 34 };
const inp = { width: '100%', padding: '7px 9px', borderRadius: 6, border: `1.5px solid ${PU_LINE}`, fontSize: 12.5, fontFamily: 'inherit', color: INK, outline: 'none', boxSizing: 'border-box', background: '#FAFAFA' };

function uid() { return 'fs' + Date.now().toString(36) + Math.random().toString(36).slice(2, 5); }

export default function JunctionMatrices({
  junctionMatrices, period = 'am', projectName = 'project',
  existingFlows = {}, onSetExisting, onPasteExisting,
  flowScenarios = [], onChangeFlowScenarios,
  timePeriods = [], activePeriodId = null,
}) {
  // Only keep well-formed junction entries. junctionMatrices normally arrives
  // as an array of { label, armNames, matrix, ... }, but a persisted project
  // can carry an older/malformed shape (a non-array, or an array with null or
  // partial slots). Guarding here means a bad shape degrades to the "no
  // matrices yet" empty state below instead of crashing the whole Results view
  // with a white page: without this, a non-array slips past the length===0
  // check (its .length is undefined, not 0) and jct.armNames throws on
  // undefined. Requiring armNames also guarantees every rendered jct has the
  // fields the table reads.
  const junctions = (Array.isArray(junctionMatrices) ? junctionMatrices : [])
    .filter(j => j && Array.isArray(j.armNames));
  const [sel, setSel] = useState(0);
  const [activeId, setActiveId] = useState(flowScenarios[0] ? flowScenarios[0].id : 'existing');
  const [pasteText, setPasteText] = useState('');
  const [showPaste, setShowPaste] = useState(false);
  const [editor, setEditor] = useState(null); // null | { mode:'add'|'edit', draft:{...} }

  if (junctions.length === 0) {
    return (
      <div style={{ background: '#fff', border: `1.5px solid ${PU_LINE}`, borderRadius: 13, padding: '22px 20px', fontSize: 13, color: MUT, lineHeight: 1.6 }}>
        No junction matrices yet. Build the schematic and run trip assignment in the map workspace
        (Census &amp; Assignment → Schematic), and the assessment junctions will appear here.
      </div>
    );
  }

  const jct = junctions[Math.min(sel, junctions.length - 1)];
  const arms = jct.armNames;
  const dev = jct.matrix || {};
  const exGrid = existingFlows[jct.label] || {};
  const devAt = (f, t) => (dev[f] && dev[f][t]) || 0;
  const exAt = (f, t) => (exGrid[f] && exGrid[f][t] != null ? exGrid[f][t] : 0);

  // Growth multiplier for a growth scenario in the currently shown peak period.
  // The stored value IS the multiplier (e.g. 1.23); empty/invalid → 1 (no change).
  const growthMultOf = fs => parseFloat((fs.growthFactors || {})[activePeriodId]) || 1;
  const ctx = { exAt, devAt, growthMult: growthMultOf };

  const activeFs = flowScenarios.find(s => s.id === activeId) || flowScenarios[0];
  const editable = isEditable(activeFs);
  const nameOf = id => { const s = flowScenarios.find(x => x.id === id); return s ? s.name : '—'; };
  const cellVal = (f, t) => activeFs ? evalFlowCell(flowScenarios, activeFs.id, f, t, ctx) : 0;

  const paste = parsePaste(pasteText, arms);
  function applyPaste() {
    if (!paste || !onPasteExisting) return;
    onPasteExisting(jct.label, arms, paste.grid);
    setPasteText(''); setShowPaste(false);
  }

  // Numeric growth multiplier per scenario id, for the Excel export (period-aware).
  const growthMultById = {};
  flowScenarios.forEach(fs => { if (fs.kind === 'growth') growthMultById[fs.id] = growthMultOf(fs); });

  // ── Flow-scenario editor helpers ──
  function openAdd() {
    setEditor({ mode: 'add', draft: { id: uid(), name: '', kind: 'growth', baseId: 'existing', growthFactors: {}, componentIds: [] } });
  }
  function openEdit(fs) {
    setEditor({ mode: 'edit', draft: { ...fs, growthFactors: { ...(fs.growthFactors || {}) }, componentIds: [...(fs.componentIds || [])] } });
  }
  function saveEditor() {
    const d = editor.draft;
    if (!d.name.trim()) return;
    const clean = d.kind === 'growth'
      ? { id: d.id, name: d.name.trim(), kind: 'growth', baseId: d.baseId, growthFactors: d.growthFactors }
      : { id: d.id, name: d.name.trim(), kind: 'sum', componentIds: d.componentIds };
    const exists = flowScenarios.some(s => s.id === d.id);
    const next = exists ? flowScenarios.map(s => s.id === d.id ? clean : s) : [...flowScenarios, clean];
    onChangeFlowScenarios && onChangeFlowScenarios(next);
    setActiveId(d.id);
    setEditor(null);
  }
  function deleteScenario(id) {
    if (isBuiltIn(id)) return;
    const next = flowScenarios.filter(s => s.id !== id);
    onChangeFlowScenarios && onChangeFlowScenarios(next);
    if (activeId === id) setActiveId('existing');
    setEditor(null);
  }
  function setDraft(patch) { setEditor(e => ({ ...e, draft: { ...e.draft, ...patch } })); }

  const badge = { fontSize: 9, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', borderRadius: 6, padding: '4px 9px' };
  // Scenarios that can be referenced by the one being edited (not itself).
  const refChoices = editor ? flowScenarios.filter(s => s.id !== editor.draft.id) : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Header + Excel export */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.07em', textTransform: 'uppercase', color: PU }}>Junction turning matrices</div>
        <button
          onClick={() => exportJunctionScenarioExcel({ junctionMatrices, existingFlows, flowScenarios, growthMultById, periodLabel: (period || 'am').toUpperCase() + ' peak', projectName })}
          title="Excel with one tab per flow scenario (growth & combined tabs linked by formula)"
          style={{ marginLeft: 'auto', fontSize: 10.5, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#fff', background: TEAL, border: 'none', borderRadius: 7, padding: '8px 13px', cursor: 'pointer', fontFamily: 'inherit' }}>
          ⬇ Export Excel
        </button>
      </div>

      {/* Junction picker */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {junctions.map((j, i) => {
          const hasEx = !!existingFlows[j.label];
          return (
            <button key={j.label + i} onClick={() => setSel(i)}
              style={{ fontSize: 12, fontWeight: 700, color: sel === i ? '#fff' : MUT, background: sel === i ? PU : '#fff', border: `1.5px solid ${sel === i ? PU : PU_LINE}`, borderRadius: 20, padding: '7px 14px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: hasEx ? TEAL : CORAL }} />
              {j.label}
            </button>
          );
        })}
      </div>

      {/* Flow-scenario tabs + add */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', border: `1.5px solid ${PU_LINE}`, borderRadius: 8, overflow: 'hidden', flexWrap: 'wrap' }}>
          {flowScenarios.map((fs, i) => (
            <button key={fs.id} onClick={() => setActiveId(fs.id)}
              style={{ fontSize: 12, fontWeight: 800, padding: '8px 14px', border: 'none', borderLeft: i ? `1.5px solid ${PU_LINE}` : 'none', background: activeId === fs.id ? PU : '#fff', color: activeId === fs.id ? '#fff' : MUT, cursor: 'pointer', fontFamily: 'inherit' }}>
              {fs.name}
            </button>
          ))}
        </div>
        {onChangeFlowScenarios && (
          <button onClick={openAdd}
            style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.03em', textTransform: 'uppercase', color: PU, background: PU_SOFT, border: `1.5px dashed ${PU2}`, borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontFamily: 'inherit' }}>
            ＋ Add flow scenario
          </button>
        )}
      </div>

      {/* Flow-scenario editor */}
      {editor && (() => {
        const d = editor.draft;
        return (
          <div style={{ background: '#fff', border: `1.5px solid ${PU2}`, borderRadius: 10, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: PU }}>{editor.mode === 'add' ? 'New flow scenario' : 'Edit flow scenario'}</div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: MUT, marginBottom: 4 }}>Name</div>
              <input value={d.name} onChange={e => setDraft({ name: e.target.value })} placeholder="e.g. Future year (2036)" style={{ ...inp, maxWidth: 320 }} />
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: MUT, marginBottom: 4 }}>How is it built?</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[{ k: 'growth', label: '📈 Growth (base × factor)' }, { k: 'sum', label: '➕ Combine (add together)' }].map(o => (
                  <button key={o.k} onClick={() => setDraft({ kind: o.k })}
                    style={{ padding: '8px 12px', borderRadius: 6, border: `1.5px solid ${d.kind === o.k ? PU : PU_LINE}`, background: d.kind === o.k ? PU : '#fff', color: d.kind === o.k ? '#fff' : MUT, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            {d.kind === 'growth' ? (
              <>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: MUT, marginBottom: 4 }}>Multiply this scenario</div>
                  <select value={d.baseId} onChange={e => setDraft({ baseId: e.target.value })} style={{ ...inp, maxWidth: 320, cursor: 'pointer' }}>
                    {refChoices.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: MUT, marginBottom: 2 }}>Growth factor (multiplier per period)</div>
                  <div style={{ fontSize: 11, color: MUT, marginBottom: 8 }}>Each figure is multiplied by this value (1.00 = no change).</div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {timePeriods.map(tp => {
                      const g = (d.growthFactors || {})[tp.id] || '';
                      return (
                        <div key={tp.id} style={{ background: PU_SOFT, borderRadius: 8, padding: '10px 12px' }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: PU, marginBottom: 6 }}>{tp.name}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 14, fontWeight: 800, color: PU }}>×</span>
                            <input type="number" step="0.01" min="0" value={g}
                              onChange={e => setDraft({ growthFactors: { ...d.growthFactors, [tp.id]: e.target.value } })}
                              placeholder="1.23" style={{ ...inp, width: 90, background: '#fff' }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: MUT, marginBottom: 6 }}>Add these scenarios together</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {refChoices.map(s => {
                    const on = d.componentIds.includes(s.id);
                    return (
                      <button key={s.id} onClick={() => setDraft({ componentIds: on ? d.componentIds.filter(x => x !== s.id) : [...d.componentIds, s.id] })}
                        style={{ padding: '7px 12px', borderRadius: 20, border: `1.5px solid ${on ? PU : PU_LINE}`, background: on ? PU : '#fff', color: on ? '#fff' : MUT, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                        {on ? '✓ ' : ''}{s.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {editor.mode === 'edit' && !isBuiltIn(d.id) && (
                <button onClick={() => deleteScenario(d.id)} style={{ fontSize: 11, fontWeight: 800, color: CORAL_INK, background: CORAL_SOFT, border: 'none', borderRadius: 7, padding: '8px 13px', cursor: 'pointer', fontFamily: 'inherit' }}>Delete</button>
              )}
              <button onClick={() => setEditor(null)} style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: MUT, background: '#fff', border: `1.5px solid ${PU_LINE}`, borderRadius: 7, padding: '8px 13px', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
              <button onClick={saveEditor} disabled={!d.name.trim() || (d.kind === 'sum' && d.componentIds.length === 0)}
                style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.03em', textTransform: 'uppercase', color: '#fff', background: (!d.name.trim() || (d.kind === 'sum' && d.componentIds.length === 0)) ? PU_LINE : PU, border: 'none', borderRadius: 7, padding: '8px 15px', cursor: 'pointer', fontFamily: 'inherit' }}>Save</button>
            </div>
          </div>
        );
      })()}

      {/* Status line */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 11.5, color: MUT }}>
        {editable
          ? <span style={{ ...badge, background: CORAL_SOFT, color: CORAL_INK }}>Editable</span>
          : <span style={{ ...badge, background: PU_SOFT, color: PU }}>{activeFs && activeFs.kind === 'development' ? 'Calculated' : 'Derived'}</span>}
        <span>{describe(activeFs, nameOf)}{editable ? ` (${(period || 'am').toUpperCase()} peak)` : ''}</span>
        {!editable && activeFs && !isBuiltIn(activeFs.id) && onChangeFlowScenarios && (
          <button onClick={() => openEdit(activeFs)} style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.03em', textTransform: 'uppercase', color: PU, background: '#fff', border: `1.5px solid ${PU_LINE}`, borderRadius: 6, padding: '6px 11px', cursor: 'pointer', fontFamily: 'inherit' }}>Edit</button>
        )}
        {editable && onPasteExisting && (
          <button onClick={() => setShowPaste(s => !s)} style={{ marginLeft: 'auto', fontSize: 10.5, fontWeight: 800, letterSpacing: '0.03em', textTransform: 'uppercase', color: PU, background: PU_SOFT, border: 'none', borderRadius: 6, padding: '7px 12px', cursor: 'pointer', fontFamily: 'inherit' }}>
            {showPaste ? 'Hide paste' : 'Paste from Excel'}
          </button>
        )}
      </div>

      {/* Paste box */}
      {editable && showPaste && (
        <div style={{ background: '#fff', border: `1.5px solid ${PU_LINE}`, borderRadius: 10, padding: 13 }}>
          <div style={{ fontSize: 11.5, color: MUT, marginBottom: 8, lineHeight: 1.5 }}>Copy the turning-count grid from Excel <b>including arm labels</b> (top row + left column) and paste below. Column order doesn't matter — arms are matched by label.</div>
          <textarea value={pasteText} onChange={e => setPasteText(e.target.value)} spellCheck={false}
            placeholder={`\t${arms.join('\t')}\n${arms[0]}\t\t...`}
            style={{ width: '100%', boxSizing: 'border-box', minHeight: 90, fontFamily: 'ui-monospace, monospace', fontSize: 12, lineHeight: 1.6, color: INK, border: `1.5px dashed ${PU2}`, borderRadius: 8, padding: '10px 12px', whiteSpace: 'pre', resize: 'vertical' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 9 }}>
            {paste
              ? <span style={{ fontSize: 11.5, fontWeight: 700, color: paste.badCols === 0 ? TEAL_INK : CORAL_INK }}>{paste.matchedRows} row(s) matched{paste.badCols ? ` · ${paste.badCols} label(s) not recognised` : ' · all arms matched'}</span>
              : <span style={{ fontSize: 11.5, color: MUT }}>Paste a grid to preview.</span>}
            <button onClick={applyPaste} disabled={!paste}
              style={{ marginLeft: 'auto', fontSize: 10.5, fontWeight: 800, letterSpacing: '0.03em', textTransform: 'uppercase', color: '#fff', background: paste ? TEAL : PU_LINE, border: 'none', borderRadius: 7, padding: '8px 13px', cursor: paste ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}>
              Fill matrix
            </button>
          </div>
        </div>
      )}

      {/* Matrix */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 12.5, minWidth: 380 }}>
          <thead>
            <tr>
              <th style={{ ...th, background: '#2a1150', fontSize: 10 }}>From \ To</th>
              {arms.map(a => <th key={a} style={th}>{a}</th>)}
              <th style={th}>Total</th>
            </tr>
          </thead>
          <tbody>
            {arms.map(f => {
              let rowTotal = 0;
              const cells = arms.map(t => {
                if (f === t) return <td key={t} style={{ ...td, background: '#F1EFE8', color: FAINT }}>—</td>;
                const v = cellVal(f, t);
                rowTotal += v;
                if (editable) {
                  return (
                    <td key={t} style={td}>
                      <input type="number" min="0" value={exAt(f, t) || ''} placeholder="0"
                        onChange={e => onSetExisting && onSetExisting(jct.label, f, t, num(e.target.value))}
                        style={{ width: 54, border: `1.5px solid ${PU_LINE}`, borderRadius: 6, padding: '6px 4px', textAlign: 'center', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, color: CORAL, background: '#FFFCFB' }} />
                    </td>
                  );
                }
                if (activeFs && activeFs.kind === 'sum') {
                  const parts = (activeFs.componentIds || []).map(cid => evalFlowCell(flowScenarios, cid, f, t, ctx));
                  return (
                    <td key={t} style={td}>
                      <div style={{ fontWeight: 800, color: PU, lineHeight: 1.1 }}>{v}</div>
                      <div style={{ fontSize: 9, color: MUT, fontWeight: 600, marginTop: 1 }}>{parts.join('+')}</div>
                    </td>
                  );
                }
                return <td key={t} style={{ ...td, color: v === 0 ? FAINT : INK, fontWeight: v === 0 ? 400 : 600 }}>{v}</td>;
              });
              return (
                <tr key={f}>
                  <td style={rh}>{f}</td>
                  {cells}
                  <td style={{ ...td, fontWeight: 800, color: PU }}>{rowTotal}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td style={{ ...rh, background: '#FAF7FC' }}>Total</td>
              {arms.map(t => {
                let colTotal = 0;
                arms.forEach(f => { if (f !== t) colTotal += cellVal(f, t); });
                return <td key={t} style={{ ...td, background: '#FAF7FC', fontWeight: 800, color: PU }}>{colTotal}</td>;
              })}
              <td style={{ ...td, background: '#FAF7FC', fontWeight: 800, color: PU }}>
                {arms.reduce((s, f) => s + arms.reduce((s2, t) => s2 + (f !== t ? cellVal(f, t) : 0), 0), 0)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div style={{ fontSize: 11, color: MUT, lineHeight: 1.5 }}>
        Rows = approach arm, columns = exit arm. Diagonal blanked (no U-turns).
        {activeFs && activeFs.kind === 'sum' && ' Each cell shows the total, with the added components beneath.'}
      </div>
    </div>
  );
}
